/**
 * Contract Expiry Alert section (CR-7231).
 *
 * How many days before a contract's end date the customer is warned. The
 * calendar's Contract Renewal reminder falls this many days ahead, and the
 * daily expiry notification counts down inside the same window. A contract's
 * own reminder days (Supplier Profile contracts) take precedence. Mirrors the
 * Quality tab's Approval Expiry Alert card, as the ticket asked.
 *
 * CR-7231 rework (UAT 26 Oct 2026): a group admin's change reached only their
 * home site, and the card did not say which site it was editing. It now has
 * the Performance tab's "Configure settings for" picker: a Group Default that
 * every site follows, and a per-site override with its own badge and Clear.
 */

import { useState } from 'react';
import type { ContractExpiryAlertSetting } from '../../../lib/api';
import { useContractExpiryAlert } from './useContractExpiryAlert';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import SettingsScopeSelector, { type ScopeOption } from '../../settings/performance/SettingsScopeSelector';
import { AlertCircle, CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_DAYS = 30;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

const optionValue = (scope: string, id: number | null) => `${scope}:${id ?? ''}`;

/** The picker's options, from the scopes the server says this user may pick. */
export function scopeOptionsFor(setting: ContractExpiryAlertSetting): ScopeOption[] {
  return setting.scopes.map((option) => ({
    value: optionValue(option.scope, option.id),
    label: option.scope === 'group' ? `${option.name} — Group Default` : option.name,
    selection: option.id === null
      ? { scope: option.scope }
      : { scope: option.scope, id: option.id },
  }));
}

export function ContractExpiryAlertSection() {
  const { setting, loading, saving, clearing, selectScope, save, clear } = useContractExpiryAlert();
  const days = setting?.days ?? DEFAULT_DAYS;
  // What the user is typing, as a string so the field can be cleared; null
  // shows the loaded value. Never copied from the setting, so the field cannot
  // lag behind a scope that has just loaded. Validated on blur / Enter.
  const [draft, setDraft] = useState<string | null>(null);

  const commitDays = async () => {
    if (draft === null) return;
    const trimmed = draft.trim();
    const parsed = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : NaN;
    if (!Number.isInteger(parsed) || parsed < MIN_DAYS || parsed > MAX_DAYS) {
      toast.error(`Enter a whole number of days between ${MIN_DAYS} and ${MAX_DAYS}`);
      setDraft(null);
      return;
    }
    // An inheriting scope typed to the value it already shows stays inheriting.
    if (parsed !== days) await save(parsed);
    // The saved value on success, the last good one on failure.
    setDraft(null);
  };

  const options = setting ? scopeOptionsFor(setting) : [];
  // The badge says where the value came from. The platform default has no
  // "from" — it is the value itself.
  const configuring = setting?.scope;
  const badgeScope = configuring === 'platform' ? undefined : setting?.source;
  const editable = Boolean(setting?.can_edit) && !saving && !clearing && !loading;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Contract Expiry Alert
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Warn your team ahead of a contract&apos;s end date, so there is time to renew or renegotiate it.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!setting && loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !setting ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Couldn&apos;t load this setting. Reload the page to try again.
          </div>
        ) : (
          <>
            {options.length > 0 && (
              <SettingsScopeSelector
                options={options}
                value={optionValue(setting.scope, setting.id)}
                onChange={(option) => {
                  setDraft(null);
                  selectScope(option.selection);
                }}
                scope={badgeScope}
                owner={setting.source_name}
                configuring={configuring}
                loading={loading}
                onClearOverride={setting.can_edit ? clear : undefined}
                clearing={clearing}
                disabled={saving || clearing}
              />
            )}
            <div className="space-y-2">
              <Label htmlFor="contract-expiry-alert-days" className="text-sm text-muted-foreground">
                Contract Expiry Alert
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="contract-expiry-alert-days"
                  type="number"
                  inputMode="numeric"
                  min={MIN_DAYS}
                  max={MAX_DAYS}
                  step={1}
                  className="max-w-[150px]"
                  value={draft ?? String(days)}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={commitDays}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  disabled={!editable}
                  aria-describedby="contract-expiry-alert-days-help"
                />
                <span className="text-sm text-muted-foreground">Days Prior</span>
                {(saving || loading) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                )}
              </div>
              <div id="contract-expiry-alert-days-help" className="space-y-1 text-xs text-muted-foreground">
                <p>
                  The calendar&apos;s Contract Renewal reminder falls this many days before a contract
                  expires. The Group Default applies to every site that has not set its own value;
                  choose a site above to set a different value for that site only. A contract&apos;s
                  own reminder days, set on the supplier profile, take precedence.
                </p>
                <p>
                  The contract owner (or your purchasing team, when the contract has no owner) is
                  notified on the first day of the window, then 90, 60, 30, 14, 7, 3 and 1 days
                  before expiry where those fall inside it. Whole number between {MIN_DAYS} and {MAX_DAYS};
                  default {DEFAULT_DAYS}.
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
